import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { EmptyArray, arrayToObject, groupBy, min, sortBy, sum, uniqueBy } from 'lib/array/arrayUtils'
import { allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'
import { safeDivideAndCeil } from 'lib/maths/mathUtils'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { EmptyObject } from 'lib/object/objectUtils'
import moment from 'moment'
import { calculateNights } from 'tripPlanner/utils'
import type { IntlShape } from 'react-intl'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { formatSaleUnitLabel } from 'hooks/useSaleUnitFormatting'
import { getBedbankMemberAccommodationPrice } from './bedbankPricing'

export type DisplayTotals = {
  original: {
    price: number
    memberPrice: number
    value: number
    discount: number
    memberDiscount: number
    flightBundlePrice: number
    flightBundleMemberPrice: number
    flightBundleValue: number
    discountWithFlight: number
    memberDiscountWithFlight: number
  }
  totals: {
    price: number
    memberPrice: number
    value: number
    flightBundlePrice: number
    flightBundleMemberPrice: number
    flightBundleValue: number
  }
}

function getRatePrice(rate: App.BedbankRate) {
  return rate.totals.memberInclusive ? rate.totals.memberInclusive + (rate.totals.propertyFees ?? 0) : rate.totals.inclusive + (rate.totals.propertyFees ?? 0)
}

export function mapBedbankBoardCode(bedbankCode: string | undefined): App.RateBoardCode {
  switch (bedbankCode) {
    case 'allinclusive':
    case 'AI':
    case 'AL':
      return 'allinclusive'
    case 'breakfast':
    case 'BB':
      return 'breakfast'
    case 'fullboard':
    case 'FB':
      return 'fullboard'
    case 'halfboard':
    case 'HB':
      return 'halfboard'
    case 'roomonly':
    case 'RO':
      return 'roomonly'
    case 'LE':
      return 'LE'
    default:
      return 'LE'
  }
}

/**
 * Decide whether to display the rate in the offer page
 * @param rate - The rate to check
 * @returns True for all non propertyCollect rate. If the rate is property collect test AND there is no leCollect relation, since we do not show propertyCollect rate if it has corresponding leCollect rate
 */
export function shouldShowBedbankRate(rate: App.BedbankRate): boolean {
  if (rate.paymentMethod === 'propertyCollect') {
    return !rate.relations?.paymentMethods.some(relation => relation.value === 'leCollect')
  }

  return true
}

export function getCheapestBedbankHotelOnlyRate(rates: Array<App.BedbankRate>): App.BedbankRate | undefined {
  return min(
    rates.filter(rate => !rate.isFlightBundle && shouldShowBedbankRate(rate)),
    rate => rate.totals.inclusive + (rate.totals.propertyFees ?? 0),
  )
}

interface SortBedbankPackageViewsOptions {
  hasDates: boolean
  hasPromotions: boolean
}

const PROMO_SORT_PRIORITY_DIVISOR = 1000

/**
 * Orders Bedbank room types for presentation across the offer page and related surfaces.
 * Priced rooms lead by price (with promotional rates promoted), while rooms that cannot
 * be priced for the current dates or occupancy sink to the end.
 */
export function sortBedbankPackageViews(
  packageViews: Array<App.BedbankOfferPackageView>,
  { hasDates, hasPromotions }: SortBedbankPackageViewsOptions,
): Array<App.BedbankOfferPackageView> {
  return sortBy(packageViews, packageView => {
    if (packageView.cheapestRate) {
      const total = packageView.cheapestRate.totals.inclusive + (packageView.cheapestRate.totals.propertyFees ?? 0)
      // Promotional rates intentionally lead the regular price ordering.
      return packageView.cheapestRate.promotionId ? total / PROMO_SORT_PRIORITY_DIVISOR : total
    }

    if (hasPromotions) {
      return Number.MAX_SAFE_INTEGER
    }

    if (hasDates && !packageView.capacityValid) {
      return Number.MAX_SAFE_INTEGER - 1
    }

    if (!hasDates && packageView.capacityValid) {
      return Number.MIN_SAFE_INTEGER
    }

    return Number.MAX_SAFE_INTEGER
  }, 'asc')
}

export function getBedbankPackageViews(
  offer: App.BedbankOffer | undefined,
  rooms: Array<App.RoomOccupants>,
  duration: number,
  checkIn: string | undefined,
  checkOut: string | undefined,
  hotelOnlyRates: Array<App.BedbankRate>,
  hotelBundleRates: Array<App.BedbankRate>,
  promotionMinLos: number,
  isCalendarEnabled: boolean,
  promotions: Record<string, App.BedbankPromotion>,
  isFlightsSelected: boolean,
): Array<App.BedbankOfferPackageView> {
  if (!offer) {
    return EmptyArray
  }

  const hasPromotions = !!offer?.promotions.length
  const validCheckinCheckoutDates = !!(checkIn && checkOut)

  const allPackages = [...offer.packages, ...hotelOnlyRates.map(rate => rate.room)]
  const roomTypes = Array.from(new Map(allPackages.map(pkg => ([pkg.roomId, pkg]))).values())
  const ratesGroup = groupBy(hotelOnlyRates, (rate) => rate.roomId)

  const packageViews = roomTypes.map(pkg => {
    const promotionsToShow: Record<string, App.BedbankPromotion> = {}
    const payAtPropertyRates = hotelOnlyRates.filter(rate => !shouldShowBedbankRate(rate))

    const roomRates = hotelOnlyRates
      .filter(shouldShowBedbankRate)
      .filter(rate => rate.roomId === pkg.roomId)

    let sortedRates = sortBy(roomRates, rate => {
      const price = getRatePrice(rate)
      // Added especially for promotional rates https://aussiecommerce.atlassian.net/browse/HC-768
      const PROMO_SORT_PRIORITY_DIVISOR = 1000
      return rate.promotionId ? price / PROMO_SORT_PRIORITY_DIVISOR : price
    }, 'asc')

    let bundleRates = hotelBundleRates.filter(rate => rate.roomId === pkg.roomId)

    sortedRates.forEach(rate => {
      const promo = rate.promotionId ? promotions[rate.promotionId] : null
      if (promo) {
        promotionsToShow[promo.id] = promo
      }
    })

    const overrideValueAtPrice = hasPromotions && validCheckinCheckoutDates

    if (overrideValueAtPrice) {
      sortedRates = sortedRates.map((rate) => {
        if (promotions[rate.promotionId ?? '']) {
          const inclusionsAmount = pkg.sell ? getBedbankPromoExtraValue({
            price: pkg.sell.price,
            value: pkg.sell.value,
            minLos: pkg.sell.los,
            duration,
          }) : 0
          const displayedHotelValue = inclusionsAmount + rate.totals.inclusive

          return {
            ...rate,
            value: Math.round(displayedHotelValue),
            discount: Number((inclusionsAmount / displayedHotelValue).toFixed(2)),
          }
        } else return rate
      })

      bundleRates = bundleRates.map((bundleRate) => {
        if (bundleRate.promotionId && promotions[bundleRate.promotionId]) {
          const inclusionsAmount = pkg.sell ? getBedbankPromoExtraValue({
            price: pkg.sell.price,
            value: pkg.sell.value,
            minLos: pkg.sell.los,
            duration,
          }) : 0
          const displayedHotelValue = inclusionsAmount + bundleRate.totals.inclusive

          return {
            ...bundleRate,
            value: Math.round(displayedHotelValue),
            discount: Number((inclusionsAmount / displayedHotelValue).toFixed(2)),
          }
        } else return bundleRate
      })
    }

    // Promotion
    const promotionLOSActive = hasPromotions && promotionMinLos <= duration
    const showPromotionInfoOnly = hasPromotions && !validCheckinCheckoutDates

    let show = false

    // if there is a valid and applicable promotion, no checkin/checkout dates are defined,
    // we should add a "ghost" package rate to display the promotion (or at least I think that's what this code is doing)
    if (showPromotionInfoOnly && promotionLOSActive) {
      const promo = offer.promotions.find((p) => p.restrictToRooms.includes(pkg.roomId))

      if (promo && pkg.sell && promo.los <= duration) {
        const price = Math.round((pkg.sell.price / pkg.sell.los) * duration * rooms.length)
        const value = Math.round((pkg.sell.value / pkg.sell.los) * duration * rooms.length)
        const taxesAndFees = Math.round((pkg.sell.taxesAndFees / pkg.sell.los) * duration * rooms.length)
        const propertyFees = Math.round((pkg.sell.propertyFees / pkg.sell.los) * duration * rooms.length)
        const discount = Number(((pkg.sell.value - pkg.sell.price) / pkg.sell.value).toFixed(2))

        sortedRates.push({
          id: promo.id,
          promotionId: promo.id,
          supplierCode: 'S0',
          boardCode: 'LE',
          optionId: '',
          bedGroups: [],
          roomId: pkg.roomId,
          room: pkg,
          groupId: undefined,
          occupancyPricing: [],
          totals: {
            exclusive: price,
            inclusive: price,
            taxesAndFees,
            propertyFees,
            total: price + propertyFees,
          },
          value,
          nights: duration,
          discount: discount > 0.02 ? discount : 0,
          memberDiscount: 0,
          refundable: false,
          partiallyRefundable: false,
          isFlightBundle: false,
          corporate: false,
          cancellationPolicies: [],
          cancellationPolicy: {
            policies: [],
            description: [(() => {
              const intl = getCurrentIntl()
              return intl.formatMessage({
                id: '_JFg15uy0yZPgvVxL1t3',
                defaultMessage: 'If you cancel your booking, you are not eligible for a refund or credit for a future stay.',
                description: 'Non-refundable cancellation policy description for bedbank promotional rates',
              })
            })()],
          },
          inclusions: [],
          paymentMethod: 'leCollect',
          relations: {
            paymentMethods: [],
          },
          calculationMethod: 'supplied',
        })
        promotionsToShow[promo.id] = promo
        show = true
      }
    } else {
      show = true
    }

    return {
      package: pkg,
      available: !validCheckinCheckoutDates || ratesGroup.has(pkg.roomId),
      capacityValid: rooms.every(room => allRoomsOccupantsFitRoomCapacity(room, pkg.capacities.combinations, offer)),
      rates: sortedRates,
      bundleRates,
      payAtPropertyRates,
      effectiveRates: combineBedbankRates(sortedRates, bundleRates, isFlightsSelected),
      cheapestRate: sortedRates[0],
      promotions: promotionsToShow,
      show,
    }
  })

  return packageViews
}

export function getBedbankPriceAndValue(
  hotelOnlyRates: Array<App.BedbankRate>,
  roomId: string | undefined,
): { price?: number, memberPrice?: number, value?: number } {
  const rate = hotelOnlyRates.find(rate => !roomId || rate.roomId === roomId)
  if (!rate) {
    return {}
  }
  const propertyFees = rate.totals.propertyFees ?? 0
  const inclusivePrice = rate.totals.inclusive + propertyFees
  const inclusiveMemberPrice = getBedbankMemberAccommodationPrice(rate)
  const memberPrice = inclusiveMemberPrice == null ? 0 : inclusiveMemberPrice + propertyFees
  return {
    price: inclusivePrice,
    memberPrice,
    value: rate.value,
  }
}

export function getBedbankFacilitiesLimit(tagline: boolean, promotionsLength: number) {
  let limitNumberOfFacilities = 5
  if (tagline) limitNumberOfFacilities = limitNumberOfFacilities - 3
  if (promotionsLength) limitNumberOfFacilities = Math.round(limitNumberOfFacilities - (promotionsLength * 1.5))
  limitNumberOfFacilities = limitNumberOfFacilities > 0 ? limitNumberOfFacilities : 0

  return limitNumberOfFacilities
}

// Promo utils
export default function getBedbankPromoExtraValue(data: {
  value: number
  price: number
  minLos: number
  duration: number
}): number {
  return ((data.value - data.price) / data.minLos) * data.duration
}

export const filterPromotionsByTravelPeriod = (promo: App.BedbankPromotion, checkIn: string, checkOut: string) => {
  const travelPeriodFrom = new Date(promo.travelPeriod.from)
  const travelPeriodTo = new Date(promo.travelPeriod.to)
  const checkInDate = new Date(checkIn)
  const checkOutDate = new Date(checkOut)

  return travelPeriodFrom <= checkInDate && travelPeriodTo >= checkInDate && travelPeriodFrom <= checkOutDate && travelPeriodTo >= checkOutDate
}

export function filterRatePromotions(
  promotions: Array<App.BedbankPromotion>,
  checkIn?: string,
  checkOut?: string,
  anytimeSearch = false,
): Record<string, App.BedbankPromotion> {
  let filteredPromotions: Array<App.BedbankPromotion>

  if (anytimeSearch) {
    filteredPromotions = promotions.filter((promo) => {
      const travelToDate = new Date(promo.travelPeriod.to)
      const currentDate = new Date()

      return travelToDate >= currentDate // filter out promotions that have ended
    })
  } else {
    if (!checkIn || !checkOut) {
      return EmptyObject
    }
    const numberOfNights = calculateNights(checkIn, checkOut)
    filteredPromotions = promotions.filter((promo) => {
      return numberOfNights && promo.los <= numberOfNights && filterPromotionsByTravelPeriod(promo, checkIn, checkOut)
    })
  }

  return arrayToObject(filteredPromotions, promo => promo.id)
}

export function getPromotionDefaultPeriod() {
  return {
    startDate: moment().utc().add(1, 'day').format(ISO_DATE_FORMAT),
    endDate: moment().utc().add(500, 'days').format(ISO_DATE_FORMAT),
  }
}

export function displayPricingAs(perPerson?: boolean, perNight?: boolean): 'night' | 'person' | undefined {
  if (perPerson) return 'person'
  if (perNight) return 'night'
}

export function getDisplayPricingAsLabel(
  perPerson: boolean | undefined,
  perNight: boolean | undefined,
  intl: IntlShape,
): string | undefined {
  const pricingAs = displayPricingAs(perPerson, perNight)
  if (!pricingAs) return undefined

  return pricingAs === 'person'
    ? intl.formatMessage({
        id: 'WNZ9VbU7vdW9KCzjp5c-',
        defaultMessage: 'person',
        description: 'Pricing unit label for per-person pricing display in the hotels section',
      })
    : intl.formatMessage({
        id: 'ALSeKvjECVT0MMcc2DAY',
        defaultMessage: 'night',
        description: 'Pricing unit label for per-night pricing display in the hotels section',
      })
}

export function getCalendarDisplayTotalPrice({
  price,
  flightPrice,
  adultsNumber,
  roomsNumber,
  withFlights,
  displayPricingPer,
  nights,
}: {
  price: number
  flightPrice: number
  adultsNumber: number
  roomsNumber: number
  withFlights: boolean
  displayPricingPer: 'night' | 'person' | undefined
  nights: number
}): {
  total: number
  saleUnit: string
} {
  const intl = getCurrentIntl()
  let total = price
  let saleUnit = formatSaleUnitLabel(intl, 'total')
  if (withFlights) {
    total = total + flightPrice
  }
  if (displayPricingPer === 'night') {
    total = price / nights
    saleUnit = formatSaleUnitLabel(intl, 'night', { withLeadingSlash: true })
    if (withFlights) {
      total = total + (flightPrice / nights)
    }
  }
  if (displayPricingPer === 'person') {
    total = price / roomsNumber / 2
    saleUnit = formatSaleUnitLabel(intl, 'person', { withLeadingSlash: true })
    if (withFlights) {
      total = total + (flightPrice / adultsNumber)
    }
  }
  return {
    total: Math.round(total),
    saleUnit,
  }
}

export interface AggregatedMultiRoomPrices {
  hotelPrice: number
  hotelMemberPrice: number
  bundlePrice: number
  bundleMemberPrice: number
  propertyFees: number
  bundlePropertyFees: number
  taxesAndFees: number
  bundleTaxesAndFees: number
  hotelValue: number
  bundleValue: number
  hotelNights: number | undefined
}

/**
 * Aggregates pricing across multiple rooms from an array of `RoomWithData`.
 * Uses `||` (logical OR) for `memberInclusive` fallback so that rooms
 * without LuxPlus pricing correctly contribute their regular inclusive
 * price to the member total.
 */
export function aggregateBedbankMultiRoomPrices(
  roomsWithData: Array<{
    rate: App.BedbankRate
    bundleRate?: App.BedbankRate
  }>,
): AggregatedMultiRoomPrices {
  let hotelPrice = 0
  let hotelMemberPrice = 0
  let bundlePrice = 0
  let bundleMemberPrice = 0
  let propertyFees = 0
  let bundlePropertyFees = 0
  let taxesAndFees = 0
  let bundleTaxesAndFees = 0
  let hotelValue = 0
  let bundleValue = 0
  let hotelNights: number | undefined

  for (const roomData of roomsWithData) {
    const rate = roomData.rate
    const bundleRate = roomData.bundleRate
    const roomPropertyFees = rate.totals.propertyFees ?? 0

    hotelPrice += rate.totals.inclusive
    hotelMemberPrice += rate.totals.memberInclusive || rate.totals.inclusive
    bundlePrice += bundleRate?.totals.inclusive || rate.totals.inclusive
    bundleMemberPrice += bundleRate?.totals.memberInclusive || bundleRate?.totals.inclusive || rate.totals.memberInclusive || rate.totals.inclusive
    propertyFees += roomPropertyFees
    bundlePropertyFees += bundleRate?.totals.propertyFees || roomPropertyFees
    taxesAndFees += rate.totals.taxesAndFees
    bundleTaxesAndFees += bundleRate?.totals.taxesAndFees || rate.totals.taxesAndFees
    hotelValue += rate.value
    bundleValue += bundleRate?.value || 0

    if (hotelNights === undefined) {
      hotelNights = rate.nights
    }
  }

  return {
    hotelPrice,
    hotelMemberPrice,
    bundlePrice,
    bundleMemberPrice,
    propertyFees,
    bundlePropertyFees,
    taxesAndFees,
    bundleTaxesAndFees,
    hotelValue,
    bundleValue,
    hotelNights,
  }
}

export function getDisplayTotalPricePerNight(displayTotals: DisplayTotals, nights: number): DisplayTotals {
  const { original, totals } = displayTotals
  return {
    original: {
      ...original,
      price: safeDivideAndCeil(original.price, nights),
      memberPrice: original.memberPrice > 0 ? safeDivideAndCeil(original.memberPrice, nights) : 0,
      value: safeDivideAndCeil(original.value, nights),
      flightBundlePrice: safeDivideAndCeil(original.flightBundlePrice, nights),
      flightBundleValue: safeDivideAndCeil(original.flightBundleValue, nights),
    },
    totals: {
      ...totals,
      price: safeDivideAndCeil(totals.price, nights),
      memberPrice: totals.memberPrice > 0 ? safeDivideAndCeil(totals.memberPrice, nights) : 0,
      value: safeDivideAndCeil(totals.value, nights),
      flightBundlePrice: safeDivideAndCeil(original.flightBundlePrice, nights),
      flightBundleValue: safeDivideAndCeil(original.flightBundleValue, nights),
    },
  }
}

export function getDisplayTotalPrice({
  hotelPrice,
  hotelMemberPrice,
  bundlePrice,
  bundleMemberPrice,
  hotelValue,
  bundleValue,
  hotelDiscount,
  hotelMemberDiscount,
  rooms,
  flightPrice,
  displayPricingAsPerPerson,
  checkIn,
}: {
  rooms: Array<App.Occupants>
  hotelPrice?: number
  hotelMemberPrice?: number
  hotelValue?: number
  bundlePrice?: number
  bundleMemberPrice?: number
  bundleValue?: number
  hotelDiscount?: number
  hotelMemberDiscount?: number
  flightPrice?: number
  displayPricingAsPerPerson?: boolean
  checkIn?: string | moment.Moment
}): DisplayTotals {
  const adults = sum(rooms, room => room.adults)
  const originHotelPrice = hotelPrice || 0
  const originBundlePrice = bundlePrice || originHotelPrice
  const originHotelMemberPrice = hotelMemberPrice || 0
  const originBundleMemberPrice = bundleMemberPrice || originHotelMemberPrice
  const originHotelValue = hotelValue || 0
  const originHotelDiscount = hotelDiscount ?? getValueOffPercent(originHotelValue, originHotelPrice)
  const originHotelMemberDiscount = hotelMemberDiscount ?? getValueOffPercent(originHotelValue, originHotelMemberPrice)
  const originBundleValue = bundleValue || originHotelValue
  const originFlightPrice = flightPrice || 0
  const originHotelPriceWithFlight = originBundlePrice + originFlightPrice
  const originHotelMemberPriceWithFlight = originBundleMemberPrice > 0 ? originBundleMemberPrice + originFlightPrice : 0
  const originHotelValueWithFlight = originBundleValue + originFlightPrice
  const discountWithFlight = getValueOffPercent(originHotelValueWithFlight, originHotelMemberPriceWithFlight)
  const memberDiscountWithFlight = getValueOffPercent(originHotelValueWithFlight, originHotelMemberPriceWithFlight)

  const originalPrices = {
    price: originHotelPrice,
    memberPrice: originHotelMemberPrice,
    value: originHotelValue,
    flightBundlePrice: originHotelPriceWithFlight,
    flightBundleMemberPrice: originHotelMemberPriceWithFlight,
    flightBundleValue: originHotelValueWithFlight,
    discount: originHotelDiscount,
    memberDiscount: originHotelMemberDiscount,
    discountWithFlight,
    memberDiscountWithFlight,
  }

  if (checkIn && displayPricingAsPerPerson) {
    return {
      original: originalPrices,
      totals: {
        price: originalPrices.price / adults,
        memberPrice: originalPrices.memberPrice > 0 ? originalPrices.memberPrice / adults : 0,
        value: originalPrices.value / adults,
        flightBundlePrice: originalPrices.flightBundlePrice / adults,
        flightBundleMemberPrice: originalPrices.flightBundleMemberPrice > 0 ? originalPrices.flightBundleMemberPrice / adults : 0,
        flightBundleValue: originalPrices.flightBundleValue / adults,
      },
    }
  } else if (!checkIn && displayPricingAsPerPerson) {
    return {
      original: originalPrices,
      totals: {
        price: originalPrices.price / rooms.length / 2,
        memberPrice: originalPrices.memberPrice > 0 ? originalPrices.memberPrice / rooms.length / 2 : 0,
        value: originalPrices.value / rooms.length / 2,
        flightBundlePrice: originalPrices.flightBundlePrice / rooms.length / 2,
        flightBundleMemberPrice: originalPrices.flightBundleMemberPrice > 0 ? originalPrices.flightBundleMemberPrice / rooms.length / 2 : 0,
        flightBundleValue: originalPrices.flightBundleValue / rooms.length / 2,
      },
    }
  }

  return {
    original: originalPrices,
    totals: {
      price: originalPrices.price,
      memberPrice: originalPrices.memberPrice,
      value: originalPrices.value,
      flightBundlePrice: originalPrices.flightBundlePrice,
      flightBundleMemberPrice: originalPrices.flightBundleMemberPrice,
      flightBundleValue: originalPrices.flightBundleValue,
    },
  }
}

export const getBedbankRatesCheckInRange = (
  monthKey: string,
  duration: number,
) => {
  const startOfMonth = moment(monthKey).startOf('month').format(ISO_DATE_FORMAT)
  const endOfMonth = moment(monthKey).endOf('month').format(ISO_DATE_FORMAT)

  let start = moment(startOfMonth).format(ISO_DATE_FORMAT)
  let end = moment(startOfMonth).add(duration, 'days').format(ISO_DATE_FORMAT)
  const selectedMonth: Array<Record<string, string>> = []

  while (moment(start).isSameOrBefore(endOfMonth)) {
    selectedMonth.push({
      checkIn: start,
      checkOut: end,
    })
    start = moment(start).add(1, 'days').format(ISO_DATE_FORMAT)
    end = moment(end).add(1, 'days').format(ISO_DATE_FORMAT)
  }

  return selectedMonth
}

export function matchBedbankRateOrItem(
  oldRate: App.Checkout.BedbankHotelItem | App.BedbankRate,
  newRate: App.Checkout.BedbankHotelItem | App.BedbankRate,
): boolean {
  return !!(oldRate.boardCode === newRate.boardCode &&
    oldRate.refundable === newRate.refundable &&
    oldRate.roomId === newRate.roomId &&
    oldRate.isFlightBundle === newRate.isFlightBundle)
}

export function findBedbankRateMatchingItem(
  rates: Array<App.BedbankRate> | undefined,
  item: App.Checkout.BedbankHotelItem,
): App.BedbankRate | undefined {
  return rates?.find(rate =>
    rate.id === item.roomRateId &&
    rate.isFlightBundle === item.isFlightBundle &&
    rate.roomId === item.roomId,
  ) ?? rates?.find(rate => matchBedbankRateOrItem(item, rate))
}

export function combineBedbankRates(hotelOnlyRates: Array<App.BedbankRate>, bundleRates: Array<App.BedbankRate>, isFlightsEnabled: boolean) {
  if (!isFlightsEnabled) {
    return hotelOnlyRates
  }

  // uniqueBy prioritises the first instance, so bundle rates will be preferred over non-bundle rates with the same ID
  const combined = uniqueBy([...bundleRates, ...hotelOnlyRates], rate => rate.id)

  // Cheapest first
  return sortBy(combined, rate => rate.totals.total, 'asc')
}
